import React, { useState, useRef, useEffect } from 'react';
import { trackEvent } from '../../utils/analytics/analytics';
import { convertUrlToLanguage } from '../../utils/algolia/url-handling';
import { normalizeSupportedLocale } from '../../utils/routing/locale-from-pathname';
import type { IntlShape } from 'react-intl';
import {
  CORE_CONTROL_GHOST,
  CORE_MENU_ITEM,
  CORE_MENU_ITEM_ACTIVE,
  CORE_MENU_SURFACE,
} from '../../constants/corePublicTheme';

export interface LanguageSelectorProps {
  intl: IntlShape;
  /** Current locale; if missing or invalid, falls back to intl.locale so the button is never empty */
  locale?: 'en' | 'fi' | 'sv';
  className?: string;
}

/**
 * Language selector component for navigation
 * Shows current language flag and dropdown to switch languages
 * Only visible on xl screens and above
 */
export const LanguageSelector: React.FC<LanguageSelectorProps> = ({
  intl,
  locale: localeProp,
  className = '',
}) => {
  const [isLanguageMenuOpen, setIsLanguageMenuOpen] = useState(false);
  const languageMenuRef = useRef<HTMLDivElement>(null);
  // Ensure we always have a valid display locale (static SSR may pass undefined; hydration may pass wrong value)
  const locale = normalizeSupportedLocale(localeProp ?? intl?.locale);

  // Close language menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (languageMenuRef.current && !languageMenuRef.current.contains(event.target as Node)) {
        setIsLanguageMenuOpen(false);
      }
    };
    if (isLanguageMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [isLanguageMenuOpen]);

  const handleLanguageChange = (newLocale: 'en' | 'fi' | 'sv') => {
    trackEvent({
      name: 'locale_change',
      params: {
        to_locale: newLocale,
        from_locale: locale,
        surface: 'other',
      },
    });
    window.location.href = convertUrlToLanguage(window.location.pathname, newLocale, intl);
  };

  return (
    <div ref={languageMenuRef} className={`hidden xl:block relative cursor-pointer ${className}`}>
      <button
        onClick={() => setIsLanguageMenuOpen(!isLanguageMenuOpen)}
        className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg cursor-pointer ${CORE_CONTROL_GHOST}`}
        aria-label={intl.formatMessage({
          id: 'navigation.language',
          defaultMessage: 'Language',
          description: 'Button aria-label for language selector in navigation',
        })}
      >
        {locale === 'fi' && <span className="text-xs">FI</span>}
        {locale === 'sv' && <span className="text-xs">SV</span>}
        {locale === 'en' && <span className="text-xs">EN</span>}
      </button>
      {isLanguageMenuOpen && (
        <div className={`absolute top-full right-0 mt-2 w-40 py-2 z-50 ${CORE_MENU_SURFACE}`}>
          <button
            onClick={() => handleLanguageChange('fi')}
            className={`w-full flex items-center gap-2 px-4 py-2 text-sm transition-colors cursor-pointer ${
              locale === 'fi' ? CORE_MENU_ITEM_ACTIVE : CORE_MENU_ITEM
            }`}
          >
            <span>FI</span>
          </button>
          <button
            onClick={() => handleLanguageChange('sv')}
            className={`w-full flex items-center gap-2 px-4 py-2 text-sm transition-colors cursor-pointer ${
              locale === 'sv' ? CORE_MENU_ITEM_ACTIVE : CORE_MENU_ITEM
            }`}
          >
            <span>SV</span>
          </button>
          <button
            onClick={() => handleLanguageChange('en')}
            className={`w-full flex items-center gap-2 px-4 py-2 text-sm transition-colors cursor-pointer ${
              locale === 'en' ? CORE_MENU_ITEM_ACTIVE : CORE_MENU_ITEM
            }`}
          >
            <span>EN</span>
          </button>
        </div>
      )}
    </div>
  );
};
